// @ts-strict-ignore
import { useState } from "react";
import type { SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";

import { Button, DropdownButton, Tooltip } from "@aviary";
import { isEmailValid } from "@shared/components/EmailInput/EmailValidation/EmailValidation";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";
import { DesignationDropdown } from "@unauthenticated/shared/components/DesignationDropdown/DesignationDropdown";
import { DesignationTooltip } from "@unauthenticated/shared/components/DesignationTooltip/DesignationTooltip";
import { FirstNameInput } from "@unauthenticated/shared/components/FirstNameInput/FirstNameInput";
import { LastNameInput } from "@unauthenticated/shared/components/LastNameInput/LastNameInput";
import { IsNameValid } from "@unauthenticated/shared/components/NameValidation/NameValidation";
import { PasswordInput } from "@unauthenticated/shared/components/PasswordInput/PasswordInput";
import { isPasswordValid } from "@unauthenticated/shared/components/PasswordInput/PasswordValidation/PasswordValidation";
import { PractitionerSignUpEmailInput } from "@unauthenticated/shared/components/PractitionerSignUpEmailInput/PracitionerSignUpEmailInput";
import { TermsOfService } from "@unauthenticated/shared/components/TermsOfService/TermsOfService";
import { useGlobalConfig } from "@unauthenticated/shared/context/GlobalConfig/GlobalConfig";
import type { PractitionerType } from "@unauthenticated/shared/data/types/PractitionerType.d";
import { useAccountExistsCheck } from "@unauthenticated/shared/hooks/useAccountExistsCheck";
import { l } from "@unauthenticated/shared/locales/i18n";

import * as styles from "./PractitionerSignUpForm.styles";

type SubmitValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  practitionerTypeId: string;
  gToken: string;
  pageVersion: string;
  signUpPage: string;
  storePlatform: string;
};

type Errors = {
  email: string[];
  firstName: string[];
  lastName: string[];
  password: string[];
  practitionerTypeId: string[];
};

interface Props {
  isPeerReferral?: boolean;
  initialFirstName?: string;
  initialLastName?: string;
  initialEmail?: string;
  onSubmit: (values: SubmitValues) => void;
  displayDesignation?: boolean;
  requireDesignation?: boolean;
  practitionerTypes?: PractitionerType[];
  designationId: string;
  setDesignationId: (string) => void;
  errors?: Errors;
  isLoading: boolean;
  pageVersion: string;
  signUpPage: string;
  onSignInRedirect?: () => void;
}

const PractitionerSignUpForm = ({
  isPeerReferral,
  initialFirstName = "",
  initialLastName = "",
  initialEmail = "",
  displayDesignation,
  requireDesignation,
  practitionerTypes,
  designationId,
  setDesignationId,
  onSubmit,
  isLoading,
  errors,
  pageVersion,
  signUpPage,
  onSignInRedirect,
}: Props) => {
  const { emailRegex } = useGlobalConfig();
  const { storePlatform } = useSharedGlobalConfig();

  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const [firstName, setFirstName] = useState(initialFirstName);
  const [firstNameDirty, setFirstNameDirty] = useState(false);
  const [lastName, setLastName] = useState(initialLastName);
  const [lastNameDirty, setLastNameDirty] = useState(false);
  const [email, setEmail] = useState(initialEmail);
  const [emailDirty, setEmailDirty] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordDirty, setPasswordDirty] = useState(false);

  const {
    isAccountExists,
    setIsAccountExists,
    handleCheckIfAccountExists,
    checkIfAccountExistsWithRecaptcha,
  } = useAccountExistsCheck();

  const handleEmailInputBlur = e => {
    setEmailDirty(true);
    if (e?.target.value) {
      setEmail(e.target.value);
      handleCheckIfAccountExists(e.target.value);
    }
  };

  const areFieldsValid = () => {
    const passwordValid = isPasswordValid(password);
    const firstNameValid = IsNameValid(firstName);
    const lastNameValid = IsNameValid(lastName);
    const emailValid = isEmailValid(email, emailRegex) && !isAccountExists;

    return passwordValid && firstNameValid && lastNameValid && emailValid;
  };

  const isDesignationValid = () => {
    const needsValidDesignation = displayDesignation || requireDesignation;
    const designationValid = needsValidDesignation ? !!designationId : true;

    return designationValid;
  };

  const isFormValid = () => areFieldsValid() && isDesignationValid();

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    if (!isFormValid()) {
      setFirstNameDirty(true);
      setLastNameDirty(true);
      setEmailDirty(true);
      setPasswordDirty(true);

      return;
    }

    gRecaptchaExecute(gToken => {
      onSubmit({
        firstName,
        lastName,
        email,
        password,
        practitionerTypeId: designationId,
        gToken,
        pageVersion,
        signUpPage,
        storePlatform,
      });
    });
  };

  const renderDesignationDropdown = () =>
    displayDesignation && (
      <div css={styles.designationRow}>
        <DesignationDropdown
          practitionerTypes={practitionerTypes}
          setValue={(id: string) => {
            setDesignationId(id);
          }}
          css={styles.marginBottom}
        >
          <DropdownButton
            isLoading={isLoading}
            css={styles.textAlign}
            data-e2e="practitioner-type-trigger"
          >
            {getTriggerText()}
          </DropdownButton>
        </DesignationDropdown>
        {!!tablet.greaterThan && <DesignationTooltip />}
      </div>
    );

  const getTriggerText = () => {
    const designation = practitionerTypes.find(type => type.id === designationId);

    return designation?.description || t(l.common.IAmA);
  };

  const showTooltip = () => requireDesignation && !isDesignationValid() && areFieldsValid();

  return (
    <form
      onSubmit={handleSubmit}
      css={styles.form}
      noValidate
      data-e2e="practitioner-sign-up-form"
      data-gtm-id="prac-sign-up-form"
    >
      <div css={styles.Row}>
        <FirstNameInput
          data-testid="first-name-input"
          disabled={!!initialFirstName && !isPeerReferral}
          value={firstName}
          onChange={e => setFirstName(e.target.value)}
          wrapperStyles={styles.margin}
          required
          isDirty={firstNameDirty}
          onBlur={() => {
            setFirstNameDirty(true);
          }}
          errors={errors?.firstName}
          isLoading={isLoading}
        />
        <LastNameInput
          data-testid="last-name-input"
          disabled={!!initialLastName && !isPeerReferral}
          value={lastName}
          onChange={e => setLastName(e.target.value)}
          required
          isDirty={lastNameDirty}
          onBlur={() => {
            setLastNameDirty(true);
          }}
          errors={errors?.lastName}
          isLoading={isLoading}
        />
      </div>
      <PractitionerSignUpEmailInput
        data-testid="email-input"
        checkIfAccountExistsWithRecaptcha={checkIfAccountExistsWithRecaptcha}
        isAccountExists={isAccountExists}
        setIsAccountExists={setIsAccountExists}
        handleCheckIfAccountExists={handleCheckIfAccountExists}
        setEmail={setEmail}
        disabled={!!initialEmail && !isPeerReferral}
        required
        value={email}
        onChange={e => setEmail(e.target.value)}
        wrapperStyles={styles.marginBottom}
        onBlur={handleEmailInputBlur}
        isDirty={emailDirty}
        isLoading={isLoading}
        onSignInRedirect={onSignInRedirect}
      />
      <PasswordInput
        value={password}
        onChange={e => setPassword(e.target.value)}
        required
        isDirty={passwordDirty}
        onFocus={() => setPasswordDirty(true)}
        errors={errors?.password}
        isLoading={isLoading}
      />
      {renderDesignationDropdown()}
      <TermsOfService css={styles.serviceTerms} />
      <Tooltip
        isTooltipHidden={!showTooltip()}
        placement="bottom"
        tooltipContent={t(l.practitionerSignUp.SelectPractitioner)}
        data-testid="disabled-submit-tooltip"
      >
        <Button
          type="submit"
          disabled={!isFormValid()}
          isLoading={isLoading}
          data-e2e="create-practitioner-button"
        >
          {t(l.common.CreateMyAccount)}
        </Button>
      </Tooltip>
    </form>
  );
};

export { PractitionerSignUpForm };
export type { SubmitValues };
